import React, {
	ChangeEvent,
	FormEvent,
	Reducer,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	BearingCalculationData,
	BearingTypeCode,
	InstallationCalculationPayload,
	InstallationCalculationType,
	TimkenBearingMaterial,
	TimkenUnit,
} from '../../../components/bearing-search/types/types';
import { bearingIsMap, text, unitMap } from '../../data';
import installationFormReducer, {
	InstallationFormState,
	InstallationFormAction,
	initialInstallationFormState,
} from './reducer';
import {
	FRICTION_COEFFICIENT_MAX,
	FRICTION_COEFFICIENT_MIN,
	INSTALLATION_FIT_MAX,
	INSTALLATION_FIT_MIN,
	POISSON_RATIO_MAX,
	POISSON_RATIO_MIN,
	YOUNGS_MODULUS_MIN,
	bearingMaterials,
	getInstallationCalculation,
} from '../../../components/bearing-search/api/api';
import { SelectOption } from '../../types/types';
import NumberInput from '../../../components/bearing-search/components/forms/NumberInput';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';
import CalculationResults from '../../components/CalculationResults';
import { getTooltip } from '../../utils/engineering-tools';
import { trackResults, updateActionParams } from '../../utils/analytics';
import { convertLength } from '../../utils/validations';

interface InstallationFormProps {
	unit: TimkenUnit;
	type: InstallationCalculationType;
	bearing: BearingPartState;
	onCalculate: ( type: InstallationCalculationType, status: boolean ) => void;
}

const InstallationForm: React.FC<InstallationFormProps> = ( {
	unit,
	type,
	bearing,
	onCalculate,
} ) => {
	const [ isValidated, setIsValidated ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ materialList, setMaterialList ] = useState<SelectOption<string>[]>( [] );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);

	const {
		partNumber,
		cupPartNumber,
		bearingType,
		bearingSubType,
		bore,
		outerDiameter,
	} = bearing;

	const initialValidation = new Map( initialInstallationFormState.validation );

	if ( bearingType !== BearingTypeCode.TRB ) {
		initialValidation.delete( 'axialLoad' );
	}

	if ( type === 'Inner' ) {
		initialValidation.delete( 'housingOuterDiameter' );
	} else {
		initialValidation.delete( 'shaftInnerDiameter' );
	}

	const initialState: InstallationFormState = {
		...initialInstallationFormState,
		unit,
		type,
		partNumber,
		cupPartNumber,
		bearingType,
		bearingID: {
			M: bore || Infinity,
			I: convertLength( bore || Infinity ),
		},
		bearingOD: {
			M: outerDiameter || 0,
			I: convertLength( outerDiameter || 0 ),
		},
		bearingIs: bearingIsMap[ bearingSubType ] || 0,
		validation: initialValidation,
	};

	const [ formState, dispatch ] = useReducer<
		Reducer<InstallationFormState, InstallationFormAction>
	>( installationFormReducer, initialState );

	useEffect( () => {
		// Build materials options
		setMaterialList(
			bearingMaterials.map( ( material: TimkenBearingMaterial ) => {
				return {
					id: `${ type.toLowerCase() }_material_${ material.id }`,
					value: material.name,
					label: material.name,
				} as SelectOption<string>;
			} ),
		);

		selectMaterial( bearingMaterials[ 0 ] );
	}, [] );

	useEffect( () => {
		dispatch( {
			type: 'change_unit',
			payload: unit,
		} );
	}, [ unit ] );

	useEffect( () => {
		onCalculate( type, isCalculated && ! isResultsLoading );
	}, [ isCalculated, isResultsLoading ] );

	const selectMaterial = ( material?: TimkenBearingMaterial ) => {
		if ( material ) {
			dispatch( {
				type: 'change_material',
				payload: material,
			} );
		}
	};

	const clearSearch = () => {
		setIsValidated( false );
		setIsCalculated( false );

		dispatch( {
			type: 'reset',
			payload: initialState,
		} );

		selectMaterial( bearingMaterials[ 0 ] );
	};

	const handleFormChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		setIsValidated( true );

		if ( formState.requestData ) {
			const request: Partial<InstallationCalculationPayload> =
				formState.requestData;

			setIsCalculated( true );
			setIsResultsLoading( true );

			getInstallationCalculation( request )
				.then( ( response ) => {
					updateActionParams( 'bearingInstallationCalculations', bearing );

					let reportData: Array<BearingCalculationData | null> = [];

					if ( response.report && response.report.length > 0 ) {
						reportData = response.report[ 0 ].data;
					}

					setResults( reportData );
					trackResults( 'bearingInstallationCalculations', request, reportData.length, bearing.marketPlaceDescription );
				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const formId = `installation_${ type.toLowerCase() }`;

	return (
		<div>
			<form
				className="t-engineering-tools__sub-form"
				onSubmit={handleSubmit}
				onChange={handleFormChange}
			>
				<div className="t-engineering-tools__constraints">
					<h3>
						{type === 'Inner'
							? text.innerInstallCalculation
							: text.outerInstallCalculation}
					</h3>

					<div className="row form-row">
						<div className="col-6 col-md-4 col-lg-2">
							<NumberInput
								id={`${ formId }_min_fit`}
								required={true}
								label={type === 'Inner' ? text.minInnerFit : text.minOuterFit}
								tooltip={getTooltip(
									type === 'Inner' ? text.minInnerFit : text.minOuterFit,
								)}
								unitLabel={unitMap[ unit ].length}
								value={formState.minFit}
								invalid={
									isValidated && formState.validation.get( 'minFit' )?.invalid
								}
								errorMessage={formState.validation.get( 'minFit' )?.message}
								min={INSTALLATION_FIT_MIN}
								max={INSTALLATION_FIT_MAX}
								onChange={( event: ChangeEvent<HTMLInputElement> ) =>
									dispatch( {
										type: 'change_min_fit',
										payload: event.target.value,
									} )
								}
							/>
						</div>

						<div className="col-6 col-md-4 col-lg-2">
							<NumberInput
								id={`${ formId }_max_fit`}
								required={true}
								label={type === 'Inner' ? text.maxInnerFit : text.maxOuterFit}
								tooltip={getTooltip(
									type === 'Inner' ? text.maxInnerFit : text.maxOuterFit,
								)}
								unitLabel={unitMap[ unit ].length}
								value={formState.maxFit}
								invalid={
									isValidated && formState.validation.get( 'maxFit' )?.invalid
								}
								errorMessage={formState.validation.get( 'maxFit' )?.message}
								min={INSTALLATION_FIT_MIN}
								max={INSTALLATION_FIT_MAX}
								onChange={( event: ChangeEvent<HTMLInputElement> ) =>
									dispatch( {
										type: 'change_max_fit',
										payload: event.target.value,
									} )
								}
							/>
						</div>

						{formState.validation.has( 'axialLoad' ) && (
							<div className="col-6 col-md-4 col-lg-2">
								<NumberInput
									id={`${ formId }_axial_load`}
									required={true}
									label={text.axialLoad}
									tooltip={getTooltip( text.axialLoad )}
									unitLabel={unitMap[ unit ].force}
									value={formState.axialLoad}
									invalid={
										isValidated &&
										formState.validation.get( 'axialLoad' )?.invalid
									}
									errorMessage={formState.validation.get( 'axialLoad' )?.message}
									min={0}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_axial_load',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						{formState.validation.has( 'shaftInnerDiameter' ) && (
							<div className="col-6 col-md-4 col-lg-2">
								<NumberInput
									id={`${ formId }_shaft_inner_diameter`}
									required={true}
									label={text.shaftInnerDiameter}
									unitLabel={unitMap[ unit ].length}
									value={formState.shaftInnerDiameter}
									invalid={
										isValidated &&
										formState.validation.get( 'shaftInnerDiameter' )?.invalid
									}
									errorMessage={
										formState.validation.get( 'shaftInnerDiameter' )?.message
									}
									min={0}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_shaft_inner_diameter',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						{formState.validation.has( 'housingOuterDiameter' ) && (
							<div className="col-6 col-md-4 col-lg-2">
								<NumberInput
									id={`${ formId }_housing_outer_diameter`}
									required={true}
									label={text.housingOuterDiameter}
									unitLabel={unitMap[ unit ].length}
									value={formState.housingOuterDiameter}
									invalid={
										isValidated &&
										formState.validation.get( 'housingOuterDiameter' )?.invalid
									}
									errorMessage={
										formState.validation.get( 'housingOuterDiameter' )?.message
									}
									min={0}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_housing_outer_diameter',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						<div className="col-md-4 col-lg-2">
							<NumberInput
								id={`${ formId }_friction_coefficient`}
								required={true}
								label={text.frictionCoefficient}
								tooltip={getTooltip( text.frictionCoefficient )}
								value={formState.frictionCoefficient}
								invalid={
									isValidated &&
									formState.validation.get( 'frictionCoefficient' )?.invalid
								}
								errorMessage={
									formState.validation.get( 'frictionCoefficient' )?.message
								}
								min={FRICTION_COEFFICIENT_MIN}
								max={FRICTION_COEFFICIENT_MAX}
								onChange={( event: ChangeEvent<HTMLInputElement> ) =>
									dispatch( {
										type: 'change_friction_coefficient',
										payload: event.target.value,
									} )
								}
							/>
						</div>

						<div className="col-md-4 col-lg-2">
							<SelectDropdown
								id={`${ formId }_material`}
								name="material"
								required={true}
								label={
									type === 'Inner' ? text.shaftMaterial : text.housingMaterial
								}
								value={formState.material}
								options={materialList}
								onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
									dispatch( {
										type: 'change_material',
										payload: bearingMaterials.find(
											( material ) => material.name === event.target.value,
										) as TimkenBearingMaterial,
									} )
								}
							/>
						</div>
					</div>

					{formState.material === 'Custom' && (
						<div className="row form-row">
							<div className="col-md-4 col-lg-2">
								<NumberInput
									id={`${ formId }_youngs_modulus`}
									required={true}
									label={text.youngsModulus}
									unitLabel={unitMap[ unit ].pressure}
									value={formState.youngsModulus}
									invalid={
										isValidated &&
										formState.validation.get( 'youngsModulus' )?.invalid
									}
									errorMessage={
										formState.validation.get( 'youngsModulus' )?.message
									}
									min={YOUNGS_MODULUS_MIN}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_youngs_modulus',
											payload: event.target.value,
										} )
									}
								/>
							</div>

							<div className="col-md-4 col-lg-2">
								<NumberInput
									id={`${ formId }_poissons_ratio`}
									required={true}
									label={text.poissonRatio}
									value={formState.poissonRatio}
									invalid={
										isValidated &&
										formState.validation.get( 'poissonRatio' )?.invalid
									}
									errorMessage={
										formState.validation.get( 'poissonRatio' )?.message
									}
									min={POISSON_RATIO_MIN}
									max={POISSON_RATIO_MAX}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_poissons_ratio',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						</div>
					)}
				</div>

				<div className="t-engineering-tools__search-actions">
					<button
						className="btn btn--primary"
						type="submit"
						disabled={isValidated}
					>
						{text.calculate}
					</button>

					<button
						className="btn btn--secondary btn--clear-form"
						type="button"
						onClick={() => clearSearch()}
					>
						{text.clear}
					</button>
				</div>
			</form>

			{isCalculated && (
				<CalculationResults
					data={results}
					subtitle={`${ type } ${ text.calculationResultsFor } ${ bearing.bearingDimensionLabel }`}
					loading={isResultsLoading}
				/>
			)}
		</div>
	);
};

export default InstallationForm;
